// `useSwitchNetwork()` — single hook called by the toggle UI.
//
// What it does:
//   1. Begins a network transition (drives the global overlay modal).
//   2. Writes the cookie + flips the Zustand store via `setUserNetwork`.
//   3. Calls `router.replace()` so the URL bar updates to add/remove
//      the `/demo-mode/` prefix to match the new runtime network, and
//      Server Components re-render against the new cookie.
//   4. Ends the transition after a minimum display window so the
//      "Switching from X to Y" overlay never flashes for less than
//      ~1.2s (long enough to register, short enough to feel instant).
//
// What it does NOT do — handled reactively elsewhere:
//   - SDK re-init lives in `SdkProvider` (subscribes to the store).
//   - Connection re-bind + query cache clear live in
//     `NetworkSyncProvider` (subscribes to the store).

'use client';

import { clearRelayFeePayerCache } from '@morfi/backend-sdk-v2/client';
import { useRouter } from 'next/navigation';
import { teardownImpersonation } from '@/lib/impersonation/teardown';
import { logger } from '@/lib/telemetry/logger';
import { useMainnetConstructionModalStore } from '@/stores/mainnet-construction-modal';
import { useNetworkTransitionStore } from '@/stores/network-transition';

import { isNetworkConfigured } from './config';
import { isMainnetEnabled } from './mainnet-enabled';
import { DEMO_MODE_EXCEPTIONS, withDemoMode } from './preference';
import { useNetworkStore } from './store';
import type { Network } from './types';

/** Build the URL to navigate to after a network switch. The `next`
 *  network drives whether the path has `/demo-mode/` prefix — non-mainnet
 *  networks add it on demo-aware routes only (URL is canonical for the
 *  runtime network), mainnet strips it everywhere.
 *
 *  Demo-mode-exception paths (about, learn, terms, etc.) keep their
 *  bare path regardless of network — they have no demo equivalent. */
function buildPostSwitchUrl(next: Network): string {
  const url = new URL(window.location.href);
  // Resolve the bare-path equivalent first (so we evaluate the exception
  // check against the canonical path, not whatever `/demo-mode/...` form
  // we happen to be on right now).
  const bareFirstSegment = ((): string => {
    const trimmed = url.pathname.replace(/^\/+/, '');
    if (trimmed.startsWith(`${'demo-mode'}/`)) {
      return trimmed.slice(`${'demo-mode'}/`.length).split('/')[0] ?? '';
    }
    if (trimmed === 'demo-mode') return '';
    return trimmed.split('/')[0] ?? '';
  })();

  // Static brand / content / share-link surfaces have no demo variant —
  // leave the URL alone, the toggle still flips the cookie + SDK.
  if (DEMO_MODE_EXCEPTIONS.has(bareFirstSegment)) {
    return `${url.pathname}${url.search}${url.hash}`;
  }

  withDemoMode(url, next);
  return `${url.pathname}${url.search}${url.hash}`;
}

/** Minimum time the "switching…" overlay stays visible. Long enough to
 *  read, short enough to feel responsive. The actual cookie write +
 *  router.refresh + cache clear happen near-instantly; this delay is
 *  pure UX padding. */
const MIN_TRANSITION_MS = 1200;

export function useSwitchNetwork(): (next: Network) => void {
  const setUserNetwork = useNetworkStore((s) => s.setUserNetwork);
  const beginTransition = useNetworkTransitionStore((s) => s.beginTransition);
  const endTransition = useNetworkTransitionStore((s) => s.endTransition);
  const router = useRouter();

  return (next: Network): void => {
    const current = useNetworkStore.getState().current;
    if (next === 'mainnet' && !isMainnetEnabled()) {
      useMainnetConstructionModalStore.getState().setOpen(true);
      return;
    }
    if (!isNetworkConfigured(next)) {
      logger.warn('network.switch.unconfigured', { from: current, to: next });
      if (next === 'mainnet') useMainnetConstructionModalStore.getState().setOpen(true);
      return;
    }
    if (current === next) return; // no-op when toggling to the active network

    beginTransition(current, next);
    // Tear down any active engineer impersonation BEFORE flipping —
    // grants are per-network and must not survive a toggle (FR-029,
    // FR-068). Fire-and-forget the await chain so the transition
    // overlay starts immediately; the store mutation + router
    // navigation are queued to run after teardown resolves so the
    // SDK re-bind never races a stale impersonation header.
    void (async (): Promise<void> => {
      await teardownImpersonation('network_toggle');
      // Clear the cached relay fee-payer BEFORE flipping the network so
      // the next relay submission resolves the new network's fee-payer
      // (T116 — fee-payer is per-network).
      clearRelayFeePayerCache();
      setUserNetwork(next);
      // Update the URL bar so it stays canonical with the runtime
      // network: non-mainnet → `/demo-mode/` prefix added, mainnet →
      // prefix stripped. `router.replace` keeps client state (no
      // full page reload) while re-rendering Server Components
      // against the new cookie.
      router.replace(buildPostSwitchUrl(next));
      window.setTimeout(endTransition, MIN_TRANSITION_MS);
    })();
  };
}
